/**
 * ChangePasswordModal - Modal for users to change their password
 */

import { useState, type FormEvent } from 'react';
import { X, Eye, EyeOff, Loader2, Lock, Check } from 'lucide-react';
import { useUIStore } from '@stores/ui.store';
import { FormErrorBanner } from '@components/forms/FormErrorBanner';
import { ValidationError } from '@components/forms/ValidationError';
import { useValidationState } from '@hooks/useValidationState';
import { api } from '@lib/api';
import {
  createApiErrorFieldStrategy,
  handleValidationErrorWithApiFieldStrategy,
} from '@lib/validation';
import type { ChangePasswordValidationField } from '@lib/types';

export const CHANGE_PASSWORD_MODAL_ID = 'change-password';

const CHANGE_PASSWORD_VALIDATION_FIELDS: readonly ChangePasswordValidationField[] = [
  'currentPassword',
  'newPassword',
];

const CHANGE_PASSWORD_API_ERROR_FIELD_STRATEGY = createApiErrorFieldStrategy<ChangePasswordValidationField>({
  INVALID_PASSWORD: 'currentPassword',
});

export function ChangePasswordModal(): JSX.Element {
  const { activeModal, closeModal } = useUIStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    formError,
    setFormError,
    fieldErrors,
    setFieldErrors,
    resetValidation,
    clearFieldError,
    applyParsedValidation,
  } = useValidationState<ChangePasswordValidationField>();
  const [success, setSuccess] = useState(false);

  const isOpen = activeModal === CHANGE_PASSWORD_MODAL_ID;

  const resetForm = (): void => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    resetValidation();
    setSuccess(false);
  };

  const handleClose = (): void => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    resetValidation();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormError('All fields are required');
      setFieldErrors({
        currentPassword: !currentPassword ? 'Current password is required' : undefined,
        newPassword: !newPassword ? 'New password is required' : undefined,
      });
      return;
    }

    if (newPassword.length < 6) {
      setFieldErrors({ newPassword: 'New password must be at least 6 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      handleValidationErrorWithApiFieldStrategy(
        err,
        CHANGE_PASSWORD_VALIDATION_FIELDS,
        CHANGE_PASSWORD_API_ERROR_FIELD_STRATEGY,
        applyParsedValidation,
        setFieldErrors,
        setFormError,
        'Failed to change password'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-ybh-bg-secondary border border-white/[0.04] rounded-2xl w-full max-w-md overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-ybh-orange/20 flex items-center justify-center">
              <Lock size={20} className="text-ybh-orange" />
            </div>
            <h2 id="change-password-title" className="font-display text-xl text-ybh-text-primary tracking-wide">
              CHANGE PASSWORD
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-ybh-text-muted/5 hover:bg-ybh-bg-elevated flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-ybh-text-muted" />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="p-6">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 rounded-full bg-ybh-success/20 flex items-center justify-center mb-4 animate-scale-in">
                <Check size={32} className="text-ybh-success" />
              </div>
              <p className="text-lg font-medium text-ybh-text-primary">Password Changed!</p>
              <p className="text-sm text-ybh-text-muted mt-1">Your password has been updated successfully.</p>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Error message */}
            <FormErrorBanner
              message={formError}
              className="p-3 bg-ybh-error/10 border border-ybh-error/30 rounded-lg text-sm text-ybh-error"
            />

            {/* Current Password */}
            <div className="space-y-2">
              <label htmlFor="current-password" className="block text-xs font-medium text-ybh-text-muted tracking-widest uppercase">
                Current Password
              </label>
              <div className="relative">
                <input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    clearFieldError('currentPassword');
                  }}
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-ybh-bg-tertiary border-2 border-transparent text-ybh-text-primary placeholder:text-ybh-text-muted focus:border-ybh-orange focus:outline-none transition-colors"
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ybh-text-muted hover:text-ybh-text-primary transition-colors"
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <ValidationError message={fieldErrors.currentPassword} className="text-xs text-ybh-error" />
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label htmlFor="new-password" className="block text-xs font-medium text-ybh-text-muted tracking-widest uppercase">
                New Password
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    clearFieldError('newPassword');
                  }}
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-ybh-bg-tertiary border-2 border-transparent text-ybh-text-primary placeholder:text-ybh-text-muted focus:border-ybh-orange focus:outline-none transition-colors"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ybh-text-muted hover:text-ybh-text-primary transition-colors"
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <ValidationError message={fieldErrors.newPassword} className="text-xs text-ybh-error" />
              <p className="text-xs text-ybh-text-muted">Minimum 6 characters</p>
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <label htmlFor="confirm-password" className="block text-xs font-medium text-ybh-text-muted tracking-widest uppercase">
                Confirm New Password
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-ybh-bg-tertiary border-2 border-transparent text-ybh-text-primary placeholder:text-ybh-text-muted focus:border-ybh-orange focus:outline-none transition-colors"
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg font-semibold text-white bg-ybh-gradient hover:shadow-glow-orange transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Changing Password...</span>
                </>
              ) : (
                <span>Change Password</span>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
